import { useEffect, useMemo, useState } from "react";
import { Treemap, ResponsiveContainer } from "recharts";
import { Layers, Search, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import DateFilter from "./layout/DateFilter";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";

const ACCENT = "#EC4899";

const PALETTE = [
  "#3B82F6", "#10B981", "#F59E0B", "#EC4899", "#A855F7",
  "#06B6D4", "#EF4444", "#84CC16", "#94A3B8", "#0EA5E9",
  "#FB7185", "#22D3EE", "#F97316", "#34D399", "#C084FC",
];

function fmtMoney(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtMoneyCompact(n) {
  if (n === null || n === undefined || isNaN(n)) return "$0";
  const abs = Math.abs(n);
  if (abs >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

const CellContent = (props) => {
  const { x, y, width, height, name, cost, fill, onSelect } = props;
  const tooSmall = width < 60 || height < 28;
  return (
    <g style={{ cursor: "pointer" }} onClick={() => onSelect && onSelect(name)}>
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#0A0B10" strokeWidth={2} />
      {!tooSmall && (
        <>
          <text x={x + width / 2} y={y + height / 2 - 6} textAnchor="middle"
                fill="#0A0B10" fontWeight="700" fontSize={width < 100 ? 9 : 12}
                style={{ pointerEvents: "none" }}>
            {name}
          </text>
          <text x={x + width / 2} y={y + height / 2 + 12} textAnchor="middle"
                fill="#0A0B10" fontSize={width < 100 ? 9 : 11}
                style={{ pointerEvents: "none" }}>
            {fmtMoney(cost)}
          </text>
        </>
      )}
    </g>
  );
};

export default function MoldCncMaterialsCard({ showMexico = false, dept = 'MOLD' }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [data, setData] = useState({ categories: [], total_cost: 0, total_units: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [search, setSearch] = useState("");

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    return p;
  }, [filter, showMexico]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/departments/${dept}/materials`, { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  const sorted = (data.categories || []).filter((d) => d.cost > 0);
  const tree = sorted.map((d, i) => ({
    name: d.category,
    cost: d.cost,
    size: d.cost,
    fill: PALETTE[i % PALETTE.length],
  }));

  const openCategory = (name) => {
    setActiveCat(name);
    setOpen(true);
    setSearch("");
    setItems([]);
    setItemsLoading(true);
    api.get(`/departments/${dept}/materials/items`, {
      params: { ...params, category: name },
    })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setItemsLoading(false));
  };

  const Content = (props) => <CellContent {...props} onSelect={openCategory} />;

  const filteredItems = items.filter((i) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (i.item || "").toLowerCase().includes(q) ||
           (i.job || "").toLowerCase().includes(q) ||
           (i.job_name || "").toLowerCase().includes(q);
  });
  const itemsTotal = filteredItems.reduce((s, i) => s + (i.cost || 0), 0);

  return (
    <>
      <section
        className="relative bg-[#12141D] border border-border overflow-hidden p-5"
        data-testid="mold-cnc-materials-card"
      >
        <span className="absolute top-0 left-0 right-0 h-[2px]"
              style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }} />

        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Layers size={18} style={{ color: ACCENT }} />
            <h3 className="font-heading font-bold text-xl">
              {dept === "FRAME"
                ? "FRAME Materials (Welding)"
                : dept === "GFRC"
                ? "GFRC Materials"
                : dept === "FINISH"
                ? "FINISH Materials"
                : dept === "FIBERGLASS"
                ? "Fiberglass Materials"
                : "MOLD & CNC Materials"}
            </h3>
          </div>
          <span className="text-[10px] tracking-widest uppercase text-muted-foreground font-mono">
            {dept === "FRAME"
              ? "Phase = WELDING · Material Welding Usage"
              : dept === "GFRC"
              ? "Phase = GRG/GRC · Material Usage Summary"
              : dept === "FINISH"
              ? "Phase = FIN · Material Usage Summary"
              : dept === "FIBERGLASS"
              ? "Material Fiberglass Usage"
              : "Phase = MOLD or CNC · Material Usage Summary"}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Category breakdown by cost — click any tile to drill into items
        </p>

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <DateFilter value={filter} onChange={setFilter} />
          {!loading && (
            <span data-testid="mat-summary" className="ml-auto text-[10px] tracking-widest uppercase text-muted-foreground font-mono">
              <span className="text-white">{fmtMoney(data.total_cost)}</span>
              {" · "}{sorted.length} categories
              {" · "}{data.total_units.toLocaleString(undefined, { maximumFractionDigits: 0 })} units
            </span>
          )}
        </div>

        {loading && (
          <div className="h-[420px] flex items-center justify-center text-sm uppercase tracking-widest text-muted-foreground">
            Loading…
          </div>
        )}
        {!loading && tree.length === 0 && (
          <div className="h-[420px] flex items-center justify-center text-sm uppercase tracking-widest text-muted-foreground"
               data-testid="mat-empty">
            No materials in range
          </div>
        )}
        {!loading && tree.length > 0 && (
          <>
            <div className="h-[420px]" data-testid="mat-treemap">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <Treemap
                  data={tree}
                  dataKey="size"
                  stroke="#0A0B10"
                  content={<Content />}
                  isAnimationActive={false}
                />
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-5" data-testid="mat-legend">
              {sorted.map((d, i) => (
                <button
                  key={`${d.category || "blank"}-${i}`}
                  data-testid={`mat-legend-${(d.category || "blank").toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => openCategory(d.category)}
                  className="flex items-center justify-between border border-border bg-[#0A0B10] px-3 py-2 text-left hover:border-[#EC4899]/60 transition-colors"
                >
                  <span className="flex items-center gap-2 text-xs">
                    <span className="h-2 w-2" style={{ background: PALETTE[i % PALETTE.length] }} />
                    {d.category}
                  </span>
                  <span className="text-xs font-mono">{fmtMoneyCompact(d.cost)}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Drill-down modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="mat-items-modal"
          className="bg-[#12141D] border-[#EC4899]/30 max-w-4xl rounded-none p-0 text-white"
        >
          <DialogHeader className="p-5 border-b border-border">
            <DialogTitle className="font-heading text-lg tracking-tight flex items-center justify-between gap-4">
              <span>{activeCat}</span>
              <span className="text-xs font-mono font-normal text-muted-foreground">
                {filteredItems.length} items · {fmtMoney(itemsTotal)}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="p-5 border-b border-border">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="mat-items-search"
                placeholder="Search by item number or job…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-[#0A0B10] border-border text-white rounded-none focus-visible:ring-[#EC4899]"
              />
            </div>
          </div>

          <div className="max-h-[55vh] overflow-auto">
            <table className="w-full text-left border-collapse" data-testid="mat-items-table">
              <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8] bg-[#1A1C28] sticky top-0">
                <tr>
                  <th className="py-3 px-4">Item Number</th>
                  <th className="py-3 px-4">Job</th>
                  <th className="py-3 px-4">Phase</th>
                  <th className="py-3 px-4 text-right">Units</th>
                  <th className="py-3 px-4 text-right">Cost</th>
                  <th className="py-3 px-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {itemsLoading && (
                  <tr><td colSpan={6} className="py-8 text-center text-muted-foreground text-xs tracking-widest uppercase">Loading…</td></tr>
                )}
                {!itemsLoading && filteredItems.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-muted-foreground text-xs tracking-widest uppercase">No items</td></tr>
                )}
                {!itemsLoading && filteredItems.map((i, idx) => (
                  <tr key={idx} className="border-b border-border hover:bg-[#1A1C28]">
                    <td className="py-3 px-4 text-sm font-mono">{i.item || "—"}</td>
                    <td className="py-3 px-4 text-sm font-mono text-[#EC4899]">{i.job || "—"}</td>
                    <td className="py-3 px-4 text-[10px] tracking-widest uppercase text-muted-foreground">{i.phase}</td>
                    <td className="py-3 px-4 text-sm font-mono text-right">{i.units?.toLocaleString() ?? "—"}</td>
                    <td className="py-3 px-4 text-sm font-mono text-right">{fmtMoney(i.cost)}</td>
                    <td className="py-3 px-4 text-sm font-mono">{i.date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
